import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import toast from '@/plugins/toasts'
import errorHandler from '@/plugins/error-handler'
import { useTranslation } from '@/composables/useTranslation'

/**
 * Composable for unpin/pin-edit item actions.
 * Shared between All.vue (bookmarks) and Collection.vue.
 *
 * @param {Object} options
 * @param {Function} options.refreshItems - View-specific refresh function
 * @param {Function} options.getPinPath - Returns the route path for the pin form given an item
 * @param {Function} [options.getExtraPinnedCount] - Returns the extra pinned count
 *   (e.g. pinned subcollections in the current scope) to combine with the item
 *   pin count when deciding whether to skip the position dialog. Defaults to 0.
 */
export function usePinActions({ refreshItems, getPinPath, getExtraPinnedCount = () => 0 }) {
  const store = useStore()
  const router = useRouter()
  const { t } = useTranslation()

  const handleUnpin = async (item) => {
    const collectionId = item.ownerId || store.getters['collectionItems/collectionId']
    try {
      await store.dispatch('collectionItem/unpinItem', {
        collectionId,
        itemId: item.id,
      })
      toast.info(t('common.pin_removed'))
      await refreshItems()
    } catch (err) {
      errorHandler.toast(err)
    }
  }

  const handlePinEdit = async (item) => {
    const collectionId = item.ownerId || store.getters['collectionItems/collectionId']
    const totalPinned = store.getters['collectionItems/numberOfPinnedItems'] + getExtraPinnedCount()

    if (item.pinPosition != null) {
      router.push({
        path: getPinPath(item),
        query: { currentPosition: item.pinPosition, pinnedCount: totalPinned, collectionId },
      })
      return
    }

    if (totalPinned === 0) {
      try {
        await store.dispatch('collectionItem/pinItem', {
          collectionId,
          itemId: item.id,
          position: 1,
        })
        toast.info(t('common.item_pinned'))
        await refreshItems()
      } catch (err) {
        errorHandler.toast(err)
      }
      return
    }

    router.push({
      path: getPinPath(item),
      query: { pinnedCount: totalPinned, collectionId },
    })
  }

  return { handleUnpin, handlePinEdit }
}
